import { useCallback, useEffect, useRef, useState } from "react";
import type { AnswerFeedback, PublicQuestion, RaceProgress, RoomStateView } from "@shared/types";
import {
  recordRaceFinish,
  roomAnswer,
  roomQuestion,
  roomState,
  startRoom,
  type RoomSession,
} from "../lib/api";
import { connectRoomChannel, type RoomChannel } from "../lib/realtime";
import { sfx } from "../lib/sound";
import { recordRaceFinished } from "../lib/stats";
import Countdown from "../components/Countdown";
import QuestionCard from "../components/QuestionCard";
import RaceTrack, { type Lane } from "../components/RaceTrack";
import RaceCar from "../components/brand/RaceCar";
import SceneBackground from "../components/brand/SceneBackground";

type Phase = "lobby" | "countdown" | "question" | "feedback" | "waiting" | "finished";

const POLL_MS = 750;
/** Slower poll when Ably push is active — still needed to advance the lazily
 *  computed bots and as a reconciliation safety net. */
const HEARTBEAT_MS = 3000;
const FEEDBACK_MS = 1700;

interface Pending {
  finished: boolean;
  raceFinished: boolean;
  question?: PublicQuestion;
}

export default function RoomGame({
  session,
  mode,
  onHome,
}: {
  session: RoomSession;
  /** Which multiplayer entry got here — only the usage counters differ. */
  mode: "quick" | "room";
  onHome: () => void;
}) {
  const { code, playerId, playerToken } = session;

  const [state, setState] = useState<RoomStateView>(session.state);
  const [phase, setPhase] = useState<Phase>(
    session.state.status === "active" ? "countdown" : "lobby",
  );
  const [question, setQuestion] = useState<PublicQuestion | null>(null);
  const [progress, setProgress] = useState<RaceProgress>({
    correctCount: 0,
    attempts: 0,
    finishLine: session.state.finishLine,
  });
  const [feedback, setFeedback] = useState<AnswerFeedback | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [pushMode, setPushMode] = useState(false);

  const pending = useRef<Pending | null>(null);
  const advanceTimer = useRef<number | null>(null);
  const channelRef = useRef<RoomChannel | null>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const countedRef = useRef(false);

  // Optional Ably push: subscribe to the room channel if the server has it
  // enabled. Pushed snapshots update state instantly; when it's off,
  // connectRoomChannel returns null and we stay on the fast poll below.
  useEffect(() => {
    let live = true;
    connectRoomChannel(code, (s) => {
      if (live) setState(s);
    }).then((ch) => {
      if (!live) {
        ch?.close();
        return;
      }
      if (ch) {
        channelRef.current = ch;
        setPushMode(true);
      }
    });
    return () => {
      live = false;
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [code]);

  // Poll the authoritative room snapshot for the roster + live car positions,
  // and to learn when the host starts or the race ends. Stops once finished.
  // The interval slows to a heartbeat once push is active (it still drives bot
  // advancement and reconciles any missed message).
  useEffect(() => {
    if (phase === "finished") return;
    let alive = true;
    const tick = () => {
      roomState(code)
        .then((r) => {
          if (alive) setState(r.state);
        })
        .catch(() => {});
    };
    tick();
    const id = window.setInterval(tick, pushMode ? HEARTBEAT_MS : POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [code, phase, pushMode]);

  // React to status changes from the poll.
  useEffect(() => {
    if (state.status === "finished" && phaseRef.current !== "finished") {
      setPhase("finished");
      return;
    }
    if (state.status === "active" && phaseRef.current === "lobby") {
      setPhase("countdown");
    }
  }, [state.status]);

  // Count a completed multiplayer race toward the local "races" coin badge and
  // the aggregate per-mode finish tally — once, no matter which transition (poll,
  // own finish, or another racer winning) flipped us to "finished". RoomGame is
  // keyed per race, so the ref resets for the next room.
  useEffect(() => {
    if (phase === "finished" && !countedRef.current) {
      countedRef.current = true;
      recordRaceFinished();
      recordRaceFinish(mode);
    }
  }, [phase, mode]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const fetchQuestion = useCallback(() => {
    roomQuestion(playerToken)
      .then((res) => {
        if (res.finished) {
          setPhase(state.status === "finished" ? "finished" : "waiting");
          return;
        }
        setProgress(res.progress);
        setQuestion(res.question ?? null);
        setFeedback(null);
        setSelectedAnswer(null);
        setPhase("question");
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Could not load the question.");
      });
    // state.status read once is fine here; finished is also caught by polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerToken]);

  const goNext = useCallback(() => {
    if (advanceTimer.current) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    const next = pending.current;
    if (!next) return;
    pending.current = null;

    if (next.raceFinished) {
      sfx.finish();
      setPhase("finished");
      return;
    }
    if (next.finished) {
      sfx.finish();
      setPhase("waiting");
      return;
    }
    if (next.question) {
      setQuestion(next.question);
      setFeedback(null);
      setSelectedAnswer(null);
      setPhase("question");
    }
  }, []);

  const handleSubmit = useCallback(
    async (answer: string) => {
      if (submitting || phaseRef.current !== "question") return;
      setSubmitting(true);
      setSelectedAnswer(answer);
      try {
        const res = await roomAnswer(playerToken, answer);
        if (res.feedback.correct) sfx.correct();
        else sfx.wrong();
        setFeedback(res.feedback);
        setProgress(res.progress);
        pending.current = {
          finished: res.finished,
          raceFinished: res.raceFinished,
          question: res.question,
        };
        setPhase("feedback");
        advanceTimer.current = window.setTimeout(goNext, FEEDBACK_MS);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Submission failed.");
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, playerToken, goNext],
  );

  function start() {
    setError("");
    startRoom(playerToken)
      .then((r) => setState(r.state))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not start the race."),
      );
  }

  function copyCode() {
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => {});
  }

  const lanes: Lane[] = state.players.map((p) => ({
    id: p.id,
    label: p.id === playerId ? `${p.displayName} (you)` : p.displayName,
    fraction: p.fraction,
    carColor: p.carColor,
    number: p.carNumber,
    isYou: p.id === playerId,
    finished: p.finished,
  }));

  const isHost = state.hostId === playerId;

  // ----- Lobby ----------------------------------------------------------------
  if (phase === "lobby") {
    return (
      <div className="relative min-h-full">
        <SceneBackground showSpeed={false} />
        <main className="mx-auto w-full max-w-lg px-4 pb-16 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onHome}>
              ← Leave
            </button>
          </div>

          <div className="panel p-5 text-center sm:p-7">
            <h2 className="font-display text-2xl text-ink sm:text-3xl">Race lobby</h2>
            <p className="mt-1 text-sm font-semibold text-ink/60">
              Share this code so friends can join:
            </p>

            <button
              type="button"
              onClick={copyCode}
              className="mx-auto mt-4 flex items-center gap-3 rounded-2xl border-[5px] border-ink bg-sunny px-6 py-3 shadow-[0_8px_0_#1c2a63] active:translate-y-[3px] active:shadow-[0_5px_0_#1c2a63]"
              title="Tap to copy"
            >
              <span className="font-display text-4xl tracking-[0.25em] text-ink">{code}</span>
              <span className="text-sm font-bold text-ink/60">{copied ? "Copied!" : "📋"}</span>
            </button>

            <div className="mt-6 text-left">
              <div className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">
                Racers ({state.players.length})
              </div>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {state.players.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-2 rounded-2xl border-[3px] border-ink bg-white px-3 py-2 shadow-[0_4px_0_#1c2a63]"
                  >
                    <RaceCar width={46} color={p.carColor} number={p.carNumber} ariaLabel="" />
                    <span className="truncate font-bold text-ink">{p.displayName}</span>
                    {p.id === state.hostId && (
                      <span className="ml-auto rounded-full border-[2px] border-ink bg-grape px-2 py-0.5 text-[10px] font-bold text-white">
                        HOST
                      </span>
                    )}
                    {p.id === playerId && p.id !== state.hostId && (
                      <span className="ml-auto text-xs font-bold text-flame">you</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <p className="mt-4 rounded-xl border-[3px] border-flame bg-flame/10 px-3 py-2 text-sm font-bold text-flame">
                {error}
              </p>
            )}

            {isHost ? (
              <button type="button" className="btn-play mt-6 w-full" onClick={start}>
                <span aria-hidden>▶</span> START RACE
                <span className="sheen animate-shine" aria-hidden />
              </button>
            ) : (
              <p className="mt-6 font-display text-lg text-ink/60">
                Waiting for the host to start…
              </p>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ----- Finished (standings) -------------------------------------------------
  if (phase === "finished") {
    const ranked = [...state.players].sort(
      (a, b) => (a.place ?? 99) - (b.place ?? 99) || b.correctCount - a.correctCount,
    );
    const medal = (i: number) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`);
    return (
      <div className="relative min-h-full">
        <SceneBackground showSpeed={false} />
        <main className="mx-auto w-full max-w-lg px-4 pb-16 pt-6">
          <div className="panel animate-pop-in p-6 text-center sm:p-8">
            <div className="text-3xl">🏁🏆🏁</div>
            <h2 className="font-display text-3xl italic text-flame">
              {state.winner
                ? state.winner.id === playerId
                  ? "You win!"
                  : `${state.winner.displayName} wins!`
                : "Race complete!"}
            </h2>

            <div className="mt-5 flex flex-col gap-2.5 text-left">
              {ranked.map((p, i) => (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 rounded-2xl border-[3px] border-ink px-3 py-2 shadow-[0_4px_0_#1c2a63] ${
                    p.id === playerId ? "bg-sunny" : "bg-white"
                  }`}
                >
                  <span className="w-8 text-center font-display text-xl text-ink">{medal(i)}</span>
                  <RaceCar width={44} color={p.carColor} number={p.carNumber} ariaLabel="" />
                  <span className="truncate font-bold text-ink">{p.displayName}</span>
                  <span className="ml-auto tabular-nums font-bold text-ink/70">
                    {p.correctCount}/{state.finishLine}
                  </span>
                </div>
              ))}
            </div>

            <button type="button" className="btn-play mt-7 w-full" onClick={onHome}>
              <span aria-hidden>🏁</span> NEW RACE
              <span className="sheen animate-shine" aria-hidden />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ----- Racing (countdown / question / feedback / waiting) -------------------
  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={phase === "question"} />
      <main className="mx-auto w-full max-w-2xl px-4 pb-12 pt-4">
        <div className="mb-4 flex items-center justify-between gap-2">
          <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onHome}>
            ← Quit
          </button>
          <span className="coin-badge px-3 py-1 text-sm">
            <span aria-hidden>🔑</span>
            <span className="font-display tracking-widest">{code}</span>
          </span>
          <span className="coin-badge px-2.5 py-1 text-sm tabular-nums">
            <span aria-hidden>✓</span>
            {progress.correctCount}/{state.finishLine}
          </span>
        </div>

        <div className="mb-5">
          <RaceTrack lanes={lanes} />
        </div>

        {phase === "countdown" ? (
          <Countdown onDone={fetchQuestion} />
        ) : phase === "waiting" ? (
          <div className="panel p-6 text-center">
            <div className="text-4xl">🎉</div>
            <h3 className="mt-1 font-display text-2xl text-grass-dark">You finished!</h3>
            <p className="mt-1 font-semibold text-ink/60">
              Cheering on the other racers… standings appear when the race ends.
            </p>
          </div>
        ) : question ? (
          <QuestionCard
            question={question}
            feedback={feedback}
            selectedAnswer={selectedAnswer}
            submitting={submitting}
            onSubmit={handleSubmit}
            onNext={goNext}
          />
        ) : (
          <div className="panel p-6 text-center font-display text-xl text-ink">
            Loading…
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-xl border-[3px] border-flame bg-flame/10 px-3 py-2 text-center text-sm font-bold text-flame">
            {error}
          </p>
        )}
      </main>
    </div>
  );
}
